import React from "react";
import { Box, Container, Grid2 as Grid, Typography } from "@mui/material";
import ReferralReachOutForm from "./ReferralReachOutForm";
import BannerCarousel from "../../components/Banner/BannerCarousel";
import { REFERRAL_PAGE_BANNER_LIST } from "../../config/Banner";
import { Head } from "@inertiajs/react";

const ReferralTierCard = ({ amount, description }) => {
  return (
    <Box
      sx={{
        px: 1,
        py: 1,
        borderLeft: 3,
        borderLeftStyle: "solid",
        borderColor: "primary.main",
        borderRadius: 3,
        background: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: { xxs: 6, mds: 4 },
          height: { xxs: "auto", mds: 80 },
          p: "24px 12px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "100%",
            letterSpacing: 0,
            width: "10%",
            textAlign: "left",
            color: "primary.main",
          }}
        >
          {amount}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "27px",
            letterSpacing: 0,
            color: "text.semiLight",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const ReferralPage = () => {
  return (
    <Box border={0} sx={{ display: "flex", flexDirection: "column", pb: 6 }}>
      <Head>
        <title>Referral Program</title>
        <meta name="title" content="WannaCollab Referral Program" />
        <meta name="description" content="Earn by referring creators to WannaCollab." />
      </Head>

      <BannerCarousel banners={REFERRAL_PAGE_BANNER_LIST} />

      <Container sx={{ mt: 0, px: 0 }} maxWidth="mds">
        <Grid container spacing={3} columns={12} p={2}>
          <Grid item size={{ xs: 12, mds: 6.5 }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                letterSpacing: "0",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            >
              The
              <Box component="span" sx={{ color: "primary.main", display: "inline" }}>
                &nbsp;WannaCollab&nbsp;
              </Box>
              Referral Program
            </Typography>

            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: "0",
                color: "text.secondary",
                mt: 1,
              }}
            >
              Do you have friends in the industry you&apos;d like to refer to WannaCollab? Please do so and get paid!
            </Typography>

            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: "0",
                mt: 3,
                color: "text.secondary",
              }}
            >
              If accepted into the program, we will create a referral link specifically for you, and any approved members who
              signed up via your link, we will pay the amount listed below:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 4 }}>
              <ReferralTierCard amount="$200" description="Over 1 million followers across X/TikTok/Instagram" />
              <ReferralTierCard amount="$150" description="Over 500k followers across X/TikTok/Instagram" />
              <ReferralTierCard amount="$75" description="Over 100k followers across X/TikTok/Instagram" />
              <ReferralTierCard amount="$50" description="Over 25k followers across X/TikTok/Instagram" />
              <ReferralTierCard amount="$25" description="Under 25k followers across X/TikTok/Instagram" />
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, mds: 5.5 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "485 / 672",
                overflow: "hidden",
                borderRadius: 6,
              }}
            >
              <img
                src="/images/referral-page/referral_side_1.png"
                alt="Creator Profile"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "opacity 0.3s ease-in-out",
                }}
              />
            </Box>
          </Grid>
          <Grid mt={4} item size={{ xs: 12, mds: 6.5 }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                letterSpacing: 0,
                verticalAlign: "middle",
              }}
            >
              Program Requirements
            </Typography>
            <Typography
              sx={{
                maxWidth: "600px",
                wordBreak: "break-word",
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: 0,
                color: "text.secondary",
                mt: 1,
              }}
            >
              This applies for Creators, Photographer, Videographer, Producers and Brands in the adult industry. Members must
              have some establishment in the industry. We do not accept members that have never worked before.
            </Typography>

            <Typography
              sx={{
                mt: 2,
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: 0,
                color: "text.secondary",
              }}
            >
              Once a member signs up, the team will notify you and send payment.
            </Typography>
          </Grid>
          <Grid mt={{ xs: 1, sm: 4 }} item size={{ xs: 12, mds: 5.5 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: " 491 / 402",
                overflow: "hidden",
                borderRadius: 6,
              }}
            >
              <img
                src="/images/referral-page/referral_side_2.png"
                alt="Creator Profile"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "opacity 0.3s ease-in-out",
                }}
              />
            </Box>
          </Grid>
          <Grid mt={6} item size={{ xs: 12, mds: 6.5 }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                letterSpacing: 0,
                verticalAlign: "middle",
              }}
            >
              Reach out to get started
            </Typography>

            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: 0,
                color: "text.secondary",
              }}
            >
              Know someone who’d be a great fit as a WannaCollab Creator? Join our Referral Program and help expand the
              WannaCollab community.
              <br />
              <br />
              Whether you’re referring someone or joining yourself, we’ll provide everything you need to get started.
            </Typography>

            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                letterSpacing: 0,
                verticalAlign: "middle",
                mt: 6,
              }}
            >
              Email
            </Typography>
            <Typography
              component="a"
              href="mailto:Team@wannaCollab.io"
              sx={{ textDecoration: "none", pt: 0.5, display: "inline-block", color: "primary.main" }}
            >
              team@wannacollab.io
            </Typography>
          </Grid>
          <Grid mt={{ xxs: 2, mds: 6 }} item size={{ xs: 12, mds: 5.5 }} sx={{ background: "white", borderRadius: 6, p: 3 }}>
            <ReferralReachOutForm />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ReferralPage;
